import React, { useRef, useEffect } from 'react';

function interpolateGPointAtDistance(data, targetDist) {
  if (!Array.isArray(data) || data.length === 0) return null;
  if (data.length === 1) {
    return {
      lateralG: data[0]?.lateralG || 0,
      longG: data[0]?.longG || 0,
    };
  }

  const maxDist = data[data.length - 1]?.dist || 0;
  const clampedDist = Math.max(0, Math.min(targetDist, maxDist));

  let nextIndex = 1;
  while (nextIndex < data.length && (data[nextIndex]?.dist || 0) < clampedDist) {
    nextIndex += 1;
  }

  const next = data[Math.min(nextIndex, data.length - 1)];
  const prev = data[Math.max(nextIndex - 1, 0)];
  const prevDist = prev?.dist || 0;
  const nextDist = next?.dist || prevDist;
  const span = nextDist - prevDist || 1;
  const t = Math.min(1, Math.max(0, (clampedDist - prevDist) / span));
  const prevLat = prev?.lateralG || 0;
  const nextLat = next?.lateralG || prevLat;
  const prevLong = prev?.longG || 0;
  const nextLong = next?.longG || prevLong;

  return {
    lateralG: prevLat + (nextLat - prevLat) * t,
    longG: prevLong + (nextLong - prevLong) * t,
  };
}

function getPlotMaxG(data) {
  if (!Array.isArray(data) || data.length === 0) return 1;

  const peakG = data.reduce((maxValue, point) => {
    const lateralG = Math.abs(point?.lateralG || 0);
    const longG = Math.abs(point?.longG || 0);
    return Math.max(maxValue, lateralG, longG);
  }, 0);

  const paddedPeakG = peakG * 1.3;
  return Math.max(1, Math.round(paddedPeakG * 2) / 2);
}

function buildGCircleValues(maxG) {
  const circleValues = [];
  for (let g = 1; g <= maxG + 0.001; g += 0.5) {
    circleValues.push(Number(g.toFixed(1)));
  }
  return circleValues;
}

export default function GForcePlot({ data, width = 280, height = 280, crosshairPos = null }) {
  const canvasRef = useRef(null);
  const renderWidth = Math.max(1, Math.round(width));
  const renderHeight = Math.max(1, Math.round(height));
  const plotSize = Math.max(1, Math.min(renderWidth, renderHeight));
  const cx = renderWidth / 2;
  const cy = renderHeight / 2;
  const maxG = getPlotMaxG(data);
  const scale = (plotSize / 2 - 30) / maxG;
  const gCircleValues = buildGCircleValues(maxG);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data?.length) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    canvas.width = renderWidth * dpr;
    canvas.height = renderHeight * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    canvas.style.width = '100%';
    canvas.style.height = `${renderHeight}px`;
    ctx.imageSmoothingEnabled = true;

    // Background
    ctx.fillStyle = '#0a0a10';
    ctx.fillRect(0, 0, renderWidth, renderHeight);

    // G circles
    ctx.strokeStyle = '#1e1e2e';
    ctx.lineWidth = 0.5;
    for (const g of gCircleValues) {
      ctx.beginPath();
      ctx.arc(cx, cy, g * scale, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Crosshairs
    ctx.beginPath();
    ctx.moveTo(cx, 10);
    ctx.lineTo(cx, renderHeight - 10);
    ctx.moveTo(10, cy);
    ctx.lineTo(renderWidth - 10, cy);
    ctx.stroke();

    // Plot points
    for (let i = 0; i < data.length; i++) {
      const x = cx + data[i].lateralG * scale;
      const y = cy - data[i].longG * scale;
      const t = i / data.length;

      ctx.globalAlpha = 0.4;
      ctx.fillStyle = `hsl(${200 + t * 160}, 80%, 55%)`;
      ctx.beginPath();
      ctx.arc(x, y, 2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    if (crosshairPos != null && crosshairPos >= 0 && crosshairPos <= 1) {
      const maxDist = data[data.length - 1]?.dist || 0;
      const targetDist = crosshairPos * maxDist;
      const highlightPoint = interpolateGPointAtDistance(data, targetDist);

      if (highlightPoint) {
        const highlightX = cx + highlightPoint.lateralG * scale;
        const highlightY = cy - highlightPoint.longG * scale;

        ctx.beginPath();
        ctx.arc(highlightX, highlightY, 9, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.18)';
        ctx.fill();

        ctx.beginPath();
        ctx.arc(highlightX, highlightY, 5, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.strokeStyle = '#0a0a10';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    }
  }, [data, renderWidth, renderHeight, cx, cy, scale, crosshairPos, gCircleValues]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 8,
          right: 12,
          color: '#e74c3c',
          fontFamily: 'var(--font-heading)',
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.6,
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        G-FORCE
      </div>
      <div
        style={{
          position: 'absolute',
          top: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          color: '#666',
          fontFamily: 'var(--font-body)',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.4,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        BRAKING
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: 8,
          left: '50%',
          transform: 'translateX(-50%)',
          color: '#666',
          fontFamily: 'var(--font-body)',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.4,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        ACCEL
      </div>
      <div
        style={{
          position: 'absolute',
          left: 12,
          top: '50%',
          transform: 'translateY(-50%) rotate(-90deg)',
          transformOrigin: 'center',
          color: '#666',
          fontFamily: 'var(--font-body)',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.4,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        LEFT
      </div>
      <div
        style={{
          position: 'absolute',
          right: 12,
          top: '50%',
          transform: 'translateY(-50%) rotate(90deg)',
          transformOrigin: 'center',
          color: '#666',
          fontFamily: 'var(--font-body)',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.4,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        RIGHT
      </div>
      {gCircleValues.map((g) => {
        const radius = g * scale;
        return (
          <div
            key={g}
            style={{
              position: 'absolute',
              left: cx + 8,
              top: cy - radius + 4,
              color: '#8b93a7',
              fontFamily: 'var(--font-body)',
              fontSize: 11,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            {`${g}g`}
          </div>
        );
      })}
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: '100%', borderRadius: 8, border: '1px solid var(--color-border)' }}
      />
    </div>
  );
}
